import React, { useState } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail 
} from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReCAPTCHA from 'react-google-recaptcha';
import GoogleSignIn from './GoogleSignIn';
import './AuthModal.css';

const AuthModal = ({ onAuthSuccess, onClose }) => {
  // Detectar si estamos en localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [name, setName] = useState('');
  const [apellido, setApellido] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recibirNotificaciones, setRecibirNotificaciones] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingUserData, setPendingUserData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [message, setMessage] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setCaptchaValue(null);
  };

  const handleForgotPassword = () => {
    setIsForgotPassword(true);
    setCaptchaValue(null);
  };

  const handleRequestVerification = async (email) => {
    try {
      const functions = getFunctions();
      const requestEmailVerification = httpsCallable(functions, 'requestEmailVerification');
      const result = await requestEmailVerification({ email });
      console.log('✓ Email de verificación enviado');
      return result;
    } catch (err) {
      console.error('Error al solicitar verificación:', err);
      throw new Error(err.message || 'Error al enviar el email de verificación');
    }
  };

  const handleVerifyEmail = async (email, code) => {
    try {
      const functions = getFunctions();
      const verifyEmail = httpsCallable(functions, 'verifyEmail');
      const result = await verifyEmail({ email, token: code });
      console.log('✓ Email verificado correctamente');
      return result;
    } catch (err) {
      console.error('Error al verificar email:', err);
      if (err.code === 'failed-precondition') {
        throw new Error('El código de verificación ha expirado. Intenta registrarte de nuevo.');
      } else if (err.code === 'invalid-argument') {
        throw new Error('Código de verificación inválido.');
      }
      throw new Error(err.message || 'Error al verificar el email');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const db = getFirestore();

    // Flujo: Restablecer contraseña (sin captcha)
    if (isForgotPassword) {
      try {
        await sendPasswordResetEmail(auth, email);
        setMessage('Se ha enviado un correo para restablecer la contraseña.');
        setError('');
      } catch (err) {
        if (err.code === 'auth/user-not-found') {
          setError('No se encontró una cuenta con este correo electrónico.');
        } else if (err.code === 'auth/invalid-email') {
          setError('El correo electrónico no es válido.');
        } else {
          setError('Ocurrió un error al enviar el correo de restablecimiento.');
        }
      }
      return;
    }

    // Flujo: Verificar código de email
    if (isVerifying) {
      try {
        setIsLoading(true);
        if (!verificationCode || verificationCode.length !== 6) {
          setError('Por favor ingresa un código de 6 dígitos válido.');
          return;
        }

        await handleVerifyEmail(email, verificationCode);

        // Crear usuario después de verificar email
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          name: pendingUserData.name,
          apellido: pendingUserData.apellido,
          telefono: pendingUserData.telefono,
          email: user.email,
          recibirNotificaciones: pendingUserData.recibirNotificaciones,
          role: 'cliente',
          emailVerified: true,
          createdAt: new Date().toISOString(),
        });

        // Limpiar formulario
        setName('');
        setApellido('');
        setTelefono('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setRecibirNotificaciones(false);
        setVerificationCode('');
        setPendingUserData(null);
        setCaptchaValue(null);
        setIsVerifying(false);
        setMessage('¡Registro exitoso!');
        setTimeout(onAuthSuccess, 1000);
      } catch (err) {
        setError(err.message || 'Error al verificar el email.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Validar email
    if (!email || !email.includes('@')) {
      setError('Por favor ingresa un correo electrónico válido.');
      return;
    }

    // Validar contraseña mínima
    if (!password || password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    // Validar captcha en login/registro (excepto en localhost)
    if (!isLocalhost && !captchaValue) {
      setError('Por favor completa el reCAPTCHA.');
      return;
    }

    if (!isLogin) {
      // Registro: Solicitar verificación de email primero
      if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden.');
        return;
      }

      try {
        setIsLoading(true);
        // Solicitar verificación de email
        await handleRequestVerification(email);

        // Guardar datos pendientes
        setPendingUserData({
          name,
          apellido,
          telefono,
          recibirNotificaciones,
        });

        // Cambiar a modo verificación
        setIsVerifying(true);
        setError('');
        setMessage('Se ha enviado un código de verificación a tu correo. Por favor ingresalo aquí.');
      } catch (err) {
        setError(err.message || 'Error al solicitar verificación de email.');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Login
      try {
        setIsLoading(true);
        await signInWithEmailAndPassword(auth, email, password);
        onAuthSuccess();
      } catch (err) {
        if (err.code === 'auth/wrong-password') {
          setError('La contraseña es incorrecta.');
        } else if (err.code === 'auth/user-not-found') {
          setError('No se encontró una cuenta con este correo electrónico.');
        } else {
          setError('Ocurrió un error al iniciar sesión.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelVerification = () => {
    setIsVerifying(false);
    setVerificationCode('');
    setPendingUserData(null);
    setMessage('');
    setError('');
  };

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal">
        <h2>
          {isVerifying
            ? 'Verificar Email'
            : isForgotPassword
            ? 'Restablecer Contraseña'
            : isLogin
            ? 'Iniciar Sesión'
            : 'Registrarse'}
        </h2>
        {!isForgotPassword && !isVerifying && (
          <>
            <GoogleSignIn onAuthSuccess={onAuthSuccess} onClose={onClose} />
            <div className="divider">
              <span>o</span>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          {isVerifying ? (
            <>
              <div className="verification-info">
                <p>Se ha enviado un código de verificación a:</p>
                <p className="email-highlight">{email}</p>
              </div>

              <div className="form-group">
                <label htmlFor="verificationCode">Código de Verificación (6 dígitos)</label>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  required
                  disabled={isLoading}
                />
              </div>
            </>
          ) : (
            <>
              {!isForgotPassword && !isLogin && (
                <>
                  <div className="form-group">
                    <label htmlFor="name">Nombre</label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="apellido">Apellido</label>
                    <input
                      type="text"
                      id="apellido"
                      value={apellido}
                      onChange={(e) => setApellido(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="telefono">Teléfono</label>
                    <input
                      type="tel"
                      id="telefono"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label htmlFor="email">
                  {isForgotPassword ? 'Correo para restablecer' : 'Correo Electrónico'}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              {!isForgotPassword && (
                <div className="form-group">
                  <label htmlFor="password">Contraseña</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              )}
              {!isLogin && !isForgotPassword && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmar Contraseña</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              )}
              {!isLogin && !isForgotPassword && (
                <div className="form-group">
                  <label htmlFor="recibirNotificaciones">¿Desea recibir notificaciones?</label>
                  <input
                    type="checkbox"
                    id="recibirNotificaciones"
                    checked={recibirNotificaciones}
                    onChange={(e) => setRecibirNotificaciones(e.target.checked)}
                    disabled={isLoading}
                  />
                </div>
              )}

              {/* Captcha visible solo en login/registro (no en localhost) */}
              {!isForgotPassword && !isLocalhost && (
                <div className="form-group">
                  <ReCAPTCHA
                    sitekey="6LcEm7QrAAAAAK9vCdjRHa9an9AgmChyioOHaWT0"
                    onChange={(value) => setCaptchaValue(value)}
                  />
                </div>
              )}
            </>
          )}

          <button type="submit" className="custom-auth-button" disabled={isLoading}>
            {isLoading ? 'Procesando...' : isVerifying ? 'Verificar Email' : isForgotPassword ? 'Enviar Correo de Restablecimiento' : isLogin ? 'Iniciar Sesión' : 'Registrarse'}
          </button>
        </form>

        {isVerifying && (
          <button className="auth-toggle" onClick={handleCancelVerification} disabled={isLoading}>
            ← Volver al registro
          </button>
        )}

        {!isForgotPassword && !isVerifying && (
          <>
            <button className="auth-toggle" onClick={toggleAuthMode} disabled={isLoading}>
              {isLogin ? '¿No tienes cuenta? Registrarse' : '¿Ya tienes cuenta? Iniciar Sesión'}
            </button>
            <button className="forgot-password-toggle" onClick={handleForgotPassword} disabled={isLoading}>
              ¿Olvidaste tu contraseña?
            </button>
          </>
        )}

        <button className="close-modal" onClick={onClose} disabled={isLoading}>
          X
        </button>
      </div>
    </div>
  );
};

export default AuthModal;
